import React, { useRef, useState } from "react";
import { Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { useNavigate } from "react-router-dom";
import "./Css/Home.css";

export function Home() {
    const navigate = useNavigate();

    return (
        <div className="home-container">
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-content">
                    <h1>Work Smarter, Stay Organized</h1>
                    <p>Transform daily tasks into achievable, step-by-step goals.</p>
                    <Button
                        variant="contained"
                        className="hero-button"
                        onClick={() => navigate("/contact")}
                    >
                        Get Started
                    </Button>
                </div>
                <div className="hero-image">
                    <HeroVisual />
                </div>
            </section>
            {/* Services Section */}
            <section className="services">
                <h2>Our Services</h2>
                <div className="services-grid">
                    <div className="service">
                        <img src="/images/branding.jpg" alt="Branding Design" />
                        <h3>Branding Design</h3>
                        <p>
                            Shape how the world sees your business with a consistent and
                            memorable brand.
                        </p>
                    </div>
                    <div className="service">
                        <img src="/images/graphic.jpg" alt="Graphic Design" />
                        <h3>Graphic Design</h3>
                        <p>
                            Modern, bold, and creative designs tailored to your needs.
                        </p>
                    </div>
                    <div className="service">
                        <img src="/images/web.jpg" alt="Web Development" />
                        <h3>Web Development</h3>
                        <p>
                            From concept to code, we create websites that drive results.
                        </p>
                    </div>
                </div>
            </section>
            {/* Business Help Section */}
            <section className="help">
                <div className="help-text">
                    <h2>We help teams build the business of their dreams</h2>
                    <p>
                        Achieving your dream business isn’t just about hard work—it’s about
                        smart strategies, the right tools, and a team-driven approach. We
                        help you combine all three to turn your vision into reality.
                    </p>
                    <p>
                        Whether you’re launching a startup or evolving an established
                        company, we provide customized solutions that align your team’s
                        strengths with your business goals, paving the way to long-term
                        success.
                    </p>
                </div>
                <div className="help-image">
                    <img src="/images/build-team.png" alt="Team Help"/>
                </div>
            </section>
            {/* Why Choose Us */}
            <section className="choose">
                <h2>Why Choose Us</h2>
                <div className="choose-grid">
                    <div className="choose-item">
                        <img src="/images/passionate.svg" alt="Passionate" />
                        <h3>Passionate</h3>
                        <p>
                            Driven by a love for innovation, we go the extra mile to bring
                            your vision to life.
                        </p>
                    </div>
                    <div className="choose-item">
                        <img src="/images/professional.svg" alt="Professional" />
                        <h3>Professional</h3>
                        <p>
                            With years of experience and proven expertise, we provide results
                            you can trust.
                        </p>
                    </div>
                    <div className="choose-item">
                        <img src="/images/support.svg" alt="Support" />
                        <h3>Support</h3>
                        <p>
                            Your goals are our goals. We’re here every step of the way.
                        </p>
                    </div>
                </div>
            </section>
            <section className="cta">
                <h2>Get started today!</h2>
                <Button
                    variant="contained"
                    className="cta-button"
                    onClick={() => navigate("/contact")}
                >
                    Contact
                </Button>
            </section>
        </div>
    );
}

const INTRO_VIDEO = "/images/smart-task-software-logo/logo-update-2026/intro-video-new-logo.mp4";

// The hero illustration shows a website with a video player; its play button plays our intro video
function HeroVisual() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [playing, setPlaying] = useState(false);

    const play = () => {
        setPlaying(true);
        // Called straight from the click so the browser allows playback with sound
        videoRef.current?.play().catch(() => setPlaying(false));
    };

    const close = () => {
        const video = videoRef.current;
        if (video) {
            video.pause();
            video.currentTime = 0;
        }
        setPlaying(false);
    };

    const togglePause = () => {
        const video = videoRef.current;
        if (video) {
            video.paused ? video.play() : video.pause();
        }
    };

    return (
        <div className="hero-visual">
            {/* Two layers so the video can sit between them: the seated man holds his card in front of the screen */}
            <img src="/images/hero-img-back.svg" alt="Illustration of a team building a website" />
            {!playing && (
                <button className="hero-play-hit" onClick={play} aria-label="Play intro video" />
            )}
            <div className="hero-video" hidden={!playing}>
                <video
                    ref={videoRef}
                    src={INTRO_VIDEO}
                    preload="none"
                    playsInline
                    onClick={togglePause}
                    onEnded={close}
                />
                <div className="hero-video-controls">
                    <button onClick={() => videoRef.current?.requestFullscreen()} aria-label="Fullscreen">
                        <FullscreenIcon />
                    </button>
                    <button onClick={close} aria-label="Close video">
                        <CloseIcon />
                    </button>
                </div>
            </div>
            <img className="hero-visual-front" src="/images/hero-img-front.svg" alt="" aria-hidden="true" />
        </div>
    );
}
